import * as billing from "./billing";
import * as consent from "./consent";
import * as activity from "./activity";
import * as content from "./content";
import {
  OP_LABELS,
  buildSummary,
  countText,
  entityLabelOf,
  fieldsFragment,
  isUuid,
  str,
  targetKey,
  translate,
} from "./vocab";

export { targetKey } from "./vocab";

/** Mirrors TenantLog's row; `details` is jsonb and may be any JSON value. */
export interface AuditLogRow {
  id: string;
  staff_id: string | null;
  actor_type: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  details: unknown;
  created_at: string;
}

/** The kinds `resolve_audit_targets` answers (KTD4). */
export type TargetKind =
  | "tier"
  | "activity"
  | "benefit"
  | "feed_post"
  | "venue"
  | "interest"
  | "interest_category"
  | "staff_role"
  | "legal_document"
  | "communication"
  | "communication_template"
  | "tier_billing_option"
  | "referral_invite"
  | "subscription_discount_grant";

export interface Ref {
  kind: TargetKind;
  id: string;
}

/**
 * A target to resolve plus its proof: `row` is the `audit_log.id` of a row on
 * the current page that mentions it. `resolve_audit_targets` answers a ref
 * only when that row belongs to the tenant and mentions the id (KTD4).
 * Identity (dedup, cache) is still `kind:id`; `row` is not part of it.
 */
export interface TargetRef extends Ref {
  row: string;
}

export interface AuditRefs {
  /** `profiles.id` values (never `profiles.user_id`). */
  profileIds: string[];
  /** One per `kind:id`; the first row that produced it supplies `row`. */
  targets: TargetRef[];
}

export interface ProfileInfo {
  full_name: string | null;
  email: string | null;
}

/**
 * For both maps: a key with a value = resolved; a key with null = resolved,
 * not found; no key and `pending` = resolving; in `failed` = the source erred.
 * Target keys are `"kind:id"` (see `targetKey`).
 */
export interface Lookups {
  profiles: Map<string, ProfileInfo | null>;
  targets: Map<string, string | null>;
  failed: { profiles: Set<string>; targets: Set<string> };
  pending: boolean;
}

export interface AuditSummary {
  primary: string | { pending: true };
  secondary: string[];
  targetState: "ok" | "missing" | "failed";
  /** Short (8-char) id shown in mono after a missing/failed primary. */
  rawId?: string;
}

export type Summarizer = (row: AuditLogRow, lookups: Lookups) => AuditSummary;

/** Receives raw values; anything that is not uuid-shaped is ignored. */
export interface RefSink {
  person(id: unknown): void;
  target(kind: TargetKind, id: unknown): void;
}

export type RefCollector = (row: AuditLogRow, sink: RefSink) => void;

/** Each domain module exports these two maps, keyed by action code. */
interface DomainModule {
  summarizers: Partial<Record<string, Summarizer>>;
  refCollectors: Partial<Record<string, RefCollector>>;
}

const DOMAINS: readonly DomainModule[] = [billing, consent, activity, content];

const SUMMARIZERS = DOMAINS.reduce<Partial<Record<string, Summarizer>>>((acc, d) => ({ ...acc, ...d.summarizers }), {});
const REF_COLLECTORS = DOMAINS.reduce<Partial<Record<string, RefCollector>>>(
  (acc, d) => ({ ...acc, ...d.refCollectors }),
  {},
);

export const EMPTY_LOOKUPS: Lookups = {
  profiles: new Map(),
  targets: new Map(),
  failed: { profiles: new Set(), targets: new Set() },
  pending: false,
};

function entityLabel(row: AuditLogRow): string {
  return entityLabelOf(row, String(row.action ?? "") || "—");
}

/** `{entity label}` · `{op} {fields}` · `{count}`, built from `details` only. */
function genericSummary(row: AuditLogRow): AuditSummary {
  const details = row.details;
  const op = str(details, "op");
  const verb = op ? translate(OP_LABELS, op) : null;
  const opFragment = fieldsFragment(details, verb ?? "alterou") ?? verb;
  return buildSummary(entityLabel(row), [opFragment, countText(details, { one: "item", many: "itens" })]);
}

function isRenderable(summary: AuditSummary): boolean {
  if (!summary || !Array.isArray(summary.secondary)) return false;
  if (typeof summary.primary === "string") return summary.primary.trim() !== "";
  return summary.primary?.pending === true;
}

/** Never throws: a summarizer that throws or returns nothing falls back to the generic summary. */
export function summarizeAuditRow(row: AuditLogRow, lookups: Lookups): AuditSummary {
  return summarizeWith(SUMMARIZERS, row, lookups);
}

/** The dispatcher over an explicit registry; exported for tests. */
export function summarizeWith(
  registry: Partial<Record<string, Summarizer>>,
  row: AuditLogRow,
  lookups: Lookups,
): AuditSummary {
  try {
    const summarizer = registry[row.action];
    if (summarizer) {
      const summary = summarizer(row, lookups);
      if (isRenderable(summary)) return summary;
    }
  } catch {
    // fall through to the generic summary
  }
  try {
    return genericSummary(row);
  } catch {
    return { primary: entityLabel(row), secondary: [], targetState: "ok" };
  }
}

/**
 * Deduplicated refs for a page of rows; only uuid-shaped ids are kept. Each
 * target carries the id of the first row that mentioned it.
 */
export function collectAuditRefs(rows: readonly AuditLogRow[]): AuditRefs {
  return collectRefsWith(REF_COLLECTORS, rows);
}

/** The collector over an explicit registry; exported for tests. */
export function collectRefsWith(
  registry: Partial<Record<string, RefCollector>>,
  rows: readonly AuditLogRow[],
): AuditRefs {
  const profileIds = new Set<string>();
  const targets = new Map<string, TargetRef>();
  let currentRow = "";
  const sink: RefSink = {
    person(id) {
      if (isUuid(id)) profileIds.add(id);
    },
    target(kind, id) {
      // Without a row id there is no proof to send, so the ref is dropped.
      if (!isUuid(id) || currentRow === "") return;
      const key = targetKey(kind, id);
      if (!targets.has(key)) targets.set(key, { kind, id, row: currentRow });
    },
  };
  for (const row of rows) {
    const collect = registry[row.action];
    if (!collect) continue;
    currentRow = typeof row.id === "string" ? row.id : "";
    try {
      collect(row, sink);
    } catch {
      // a malformed row contributes no refs
    }
  }
  return { profileIds: Array.from(profileIds), targets: Array.from(targets.values()) };
}
